import {
  PureComponent,
  Fragment,
  ComponentType,
  PropsWithChildren,
  MouseEvent,
} from 'react';

import { injectIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import ChevronLeftIcon from '@atlaskit/icon/core/chevron-left';
import LinkExternalIcon from '@atlaskit/icon/core/link-external';
import { ButtonItem } from '@atlaskit/menu';

import {
  DevToolsContext,
  DevToolsDispatchContext,
} from 'src/components/dev-tools-loader/context';
import { settings } from 'src/settings';

import { AnalyticsEvent } from '../types';
import urls from '../urls';

import { Blog } from './blog-fetch';
import { DropdownItem, DropdownItemGroup } from './dropdown-helpers';
import { messages } from './help-menu.i18n';
import OverlayDropdown from './overlay-dropdown';
import * as styles from './overlay-dropdown.style';
import WhatsNewParentItem from './whats-new-parent-item';
import WhatsNewPostItem from './whats-new-post-item';

export type ExternalHelpMenuProps = {
  onKeyboardShortcutsActivated: () => void;
  onFeedbackModalOpen: () => void;
};

export type HelpMenuProps = ExternalHelpMenuProps & {
  attributionsUrl?: string;
  supportAdminUrl?: string;
  blogs: Blog[];
  newBlogCount: number;
  storeLastSeenBlog: () => void;
  intl: IntlShape;
  publishUiEvent: (event: AnalyticsEvent) => void;
  isAtlassian?: boolean;
};

const NoticeOfCollectionDropdownItem: ComponentType<
  PropsWithChildren<{
    isAtlassian?: boolean;
  }>
> = ({ isAtlassian, children }) => (
  <DropdownItem
    href={
      isAtlassian
        ? urls.external.noticeOfCollectionInternal()
        : urls.external.noticeOfCollectionExternal()
    }
    rel="noopener noreferrer"
  >
    {children}
  </DropdownItem>
);

class HelpMenu extends PureComponent<HelpMenuProps> {
  declare context: React.ContextType<typeof DevToolsContext>;

  static contextType = DevToolsContext;

  renderDropdownItems = (
    publishUiEvent: (event: AnalyticsEvent) => void,
    blogs: Blog[],
    newBlogCount: number,
    storeLastSeenBlog: () => void
  ) => {
    const { intl, attributionsUrl, supportAdminUrl, isAtlassian } = this.props;

    const showInternalSection =
      Boolean(supportAdminUrl) || this.context.isEnabled;

    return (
      <OverlayDropdown
        overlayContent={hideOverlay => (
          <Fragment>
            <styles.OverlayHeader onClick={hideOverlay} tabIndex={0}>
              <ChevronLeftIcon size="small" label="" spacing="spacious" />{' '}
              {intl.formatMessage(messages.whatsNewTitle)}
            </styles.OverlayHeader>
            <styles.PostsWrapper>
              {blogs.map(whatsNewItem => (
                <WhatsNewPostItem
                  key={whatsNewItem.id}
                  onClick={() => {
                    publishUiEvent({
                      action: 'clicked',
                      actionSubject: 'post',
                      actionSubjectId: 'whatsNewPost',
                      source: 'navigation',
                    });
                  }}
                  {...whatsNewItem}
                />
              ))}
            </styles.PostsWrapper>
          </Fragment>
        )}
        onOverlayHidden={storeLastSeenBlog}
      >
        {showOverlay => (
          <Fragment>
            {showInternalSection && (
              <DropdownItemGroup
                title={intl.formatMessage(messages.internalToolsSectionTitle)}
                hasSeparator
              >
                {supportAdminUrl && (
                  <DropdownItem
                    // support admin is a core page so we need the absolute URL so
                    // the app-provider-link isn't used.

                    href={`${settings().CANON_URL}${supportAdminUrl}`}
                  >
                    {intl.formatMessage(messages.supportAdminLink)}
                  </DropdownItem>
                )}

                {this.context.isEnabled && (
                  <DevToolsDispatchContext.Consumer>
                    {dispatch => (
                      <ButtonItem
                        onClick={() => dispatch({ type: 'TOGGLE_DIALOG' })}
                      >
                        Star Menu (dev tools)
                      </ButtonItem>
                    )}
                  </DevToolsDispatchContext.Consumer>
                )}
              </DropdownItemGroup>
            )}
            <DropdownItemGroup
              title={intl.formatMessage(messages.helpHeading)}
              hasSeparator
            >
              {blogs.length ? (
                <WhatsNewParentItem
                  newBlogCount={newBlogCount}
                  onClick={(e: MouseEvent<any>) => {
                    showOverlay(e);
                    publishUiEvent({
                      action: 'clicked',
                      actionSubject: 'submenu',
                      actionSubjectId: 'whatsNewSubmenu',
                      source: 'navigation',
                    });
                  }}
                />
              ) : null}
              <DropdownItem
                href={urls.external.documentation()}
                rel="noopener noreferrer"
                target="_blank"
                iconAfter={<LinkExternalIcon label="" />}
              >
                {intl.formatMessage(messages.documentationLink)}
              </DropdownItem>
              <DropdownItem
                href={urls.external.gettingGitRight()}
                rel="noopener noreferrer"
                target="_blank"
                iconAfter={<LinkExternalIcon label="" />}
              >
                {intl.formatMessage(messages.learnGitLink)}
              </DropdownItem>
              <DropdownItem onClick={this.props.onKeyboardShortcutsActivated}>
                {intl.formatMessage(messages.keyboardShortcutsButton)}
              </DropdownItem>
              <DropdownItem
                href={urls.external.tutorials()}
                rel="noopener noreferrer"
                target="_blank"
                iconAfter={<LinkExternalIcon label="" />}
              >
                {intl.formatMessage(messages.tutorialsLink)}
              </DropdownItem>
              <DropdownItem
                href={urls.external.apiDocumentation()}
                rel="noopener noreferrer"
                target="_blank"
                iconAfter={<LinkExternalIcon label="" />}
              >
                {intl.formatMessage(messages.apiLink)}
              </DropdownItem>
              <DropdownItem
                href={urls.ui.support()}
                rel="noopener noreferrer"
                target="_blank"
                iconAfter={<LinkExternalIcon label="" />}
              >
                {intl.formatMessage(messages.supportLink)}
              </DropdownItem>
              <DropdownItem onClick={this.props.onFeedbackModalOpen}>
                {intl.formatMessage(messages.giveFeedbackButton)}
              </DropdownItem>
            </DropdownItemGroup>
            <DropdownItemGroup
              title={intl.formatMessage(messages.informationHeading)}
              hasSeparator
            >
              <DropdownItem
                href={urls.external.blog()}
                rel="noopener noreferrer"
              >
                {intl.formatMessage(messages.blogLink)}
              </DropdownItem>
              <DropdownItem
                href={urls.ui.plans()}
                rel="noopener noreferrer"
                target="_blank"
                iconAfter={<LinkExternalIcon label="" />}
              >
                {intl.formatMessage(messages.plansLink)}
              </DropdownItem>
              <DropdownItem
                href={urls.external.status()}
                rel="noopener noreferrer"
                target="_blank"
                iconAfter={<LinkExternalIcon label="" />}
              >
                {intl.formatMessage(messages.siteStatusLink)}
              </DropdownItem>
              {attributionsUrl && (
                <DropdownItem
                  href={attributionsUrl}
                  rel="noopener noreferrer"
                  target="_blank"
                  iconAfter={<LinkExternalIcon label="" />}
                >
                  {intl.formatMessage(messages.attributionsPageLink)}
                </DropdownItem>
              )}
            </DropdownItemGroup>
            <DropdownItemGroup
              title={intl.formatMessage(messages.legalHeading)}
              hasSeparator
            >
              <DropdownItem
                href={urls.external.termsOfService()}
                rel="noopener noreferrer"
              >
                {intl.formatMessage(messages.termsOfServiceLink)}
              </DropdownItem>
              <NoticeOfCollectionDropdownItem isAtlassian={isAtlassian}>
                {intl.formatMessage(messages.noticeOfCollectionLink)}
              </NoticeOfCollectionDropdownItem>
              <DropdownItem
                href={urls.external.privacyPolicy()}
                rel="noopener noreferrer"
              >
                {intl.formatMessage(messages.privacyPolicyLink)}
              </DropdownItem>
            </DropdownItemGroup>
          </Fragment>
        )}
      </OverlayDropdown>
    );
  };

  render() {
    const { blogs, newBlogCount, storeLastSeenBlog, publishUiEvent } =
      this.props;

    return this.renderDropdownItems(
      publishUiEvent,
      blogs,
      newBlogCount,
      storeLastSeenBlog
    );
  }
}

// eslint-disable-next-line import/no-default-export
export default injectIntl(HelpMenu);
